import DixaIcon from '@dixa/dixa-modern-icons';
import {
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { Component } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  FieldArray,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
  WrappedFieldArrayProps,
} from 'redux-form';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import {
  FlowNodeTypes,
  OptionT,
} from '../../../powerFlowShared/components/types';
import InputField from '../../../shared/util/InputField';
import SelectField from '../../../shared/util/SelectField';
import { getRequestID } from '../../../shared/util/request';
import type {
  ContentRule,
  FlowEmailNodeContentPayloadT,
  FlowEmailNodeContentT,
} from '../nodeTypes';

type FlowEmailContentModalStatePropsT = {
  initialValues: FlowEmailNodeContentPayloadT;
  payload: FlowEmailNodeContentPayloadT;
};

type FlowEmailContentModalReduxFormPropsT = InjectedFormProps<
  ContentModalFormValues,
  PropsT
>;

type FlowEmailContentModalOwnPropsT = FlowManagerEditModalComponentT<
  FlowEmailNodeContentT,
  {}
>;

type PropsT = FlowEmailContentModalOwnPropsT & FlowEmailContentModalStatePropsT;

type ContentModalFormValues = {
  rule_type: 'any' | 'all';
  rules: ContentRule[];
};

type ValidationFieldError = {
  match?: 'Required';
  rule?: 'Required';
  type?: 'Required';
};
type ValidationFieldErrorArray = ValidationFieldError[];
type ValidationT = {
  rules?:
    | {
        _error: string;
      }
    | ValidationFieldErrorArray;
};

const validate = (values: ContentModalFormValues): FormErrors => {
  const errors: ValidationT = {};

  if (!values.rules || !values.rules.length) {
    errors.rules = {
      _error: 'At least one rule must be entered - click on "Add"',
    };
  } else {
    const ruleArrayErrors: ValidationFieldErrorArray = [];
    values.rules.forEach((rule: ContentRule, index: number) => {
      const ruleErrors: ValidationFieldError = {};

      if (!rule) {
        errors.rules = {
          _error: 'At least one rule must be entered - click on "Add".',
        };
      }
      if (!rule || !rule.type) {
        ruleErrors.type = 'Required';
        ruleArrayErrors[index] = ruleErrors;
      }
      if (!rule || !rule.rule) {
        ruleErrors.rule = 'Required';
        ruleArrayErrors[index] = ruleErrors;
      }
      if (!rule || !rule.match) {
        ruleErrors.match = 'Required';
        ruleArrayErrors[index] = ruleErrors;
      }
    });
    if (ruleArrayErrors.length) {
      errors.rules = ruleArrayErrors;
    }
  }

  return errors;
};

const ListContent = ({
  fields,
  meta: { error, submitFailed },
}: WrappedFieldArrayProps) => {
  const typeOptions = [
    {
      label: 'Subject',
      value: 'subject',
    },
    {
      label: 'Body',
      value: 'body',
    },
    {
      label: 'From',
      value: 'from',
    },
  ];

  const targetOptions = [
    {
      label: 'Contains',
      value: 'contains',
    },
    {
      label: 'Does not contain',
      value: 'not-contains',
    },
    {
      label: 'Starts with',
      value: 'prefix',
    },
    {
      label: 'Ends with',
      value: 'suffix',
    },
    {
      label: 'is',
      value: 'equals',
    },
    {
      label: 'is not',
      value: 'not-equals',
    },
  ];

  return (
    <div>
      <div className="section-header mt10 mb10">
        <h5 className="hidden">Content</h5>
      </div>
      {submitFailed && error && <span>{error}</span>}
      {fields.map((rule: string, index: number) => {
        return (
          <div key={index} className="input-field-content">
            <button
              type="button"
              className="btn btn-xm remove-content pull-right btn-remove"
              title="Remove"
              onClick={() => fields.remove(index)}
            >
              <DixaIcon icon="close" />
            </button>
            <strong className="hidden">Content Rule #{index + 1}</strong>
            <Field
              name={`${rule}.type`}
              isRequired={true}
              displayName="Field"
              component={SelectField}
              className="form-control"
              style={{
                width: '30%',
                marginRight: '1%',
              }}
            >
              <option hidden> - Select a target</option>
              {typeOptions.map((option: OptionT, index: number) => {
                return (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                );
              })}
            </Field>
            <Field
              name={`${rule}.rule`}
              isRequired={true}
              displayName="Rule"
              component={SelectField}
              className="form-control"
              style={{
                width: '30%',
                marginRight: '1%',
              }}
            >
              <option hidden> - Type of rule</option>
              {targetOptions.map((option: OptionT, index: number) => {
                return (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                );
              })}
            </Field>
            <Field
              name={`${rule}.match`}
              isRequired={true}
              type="text"
              displayName="Value"
              placeholder={`Eg. billing`}
              component={InputField}
              className="form-control"
              style={{
                width: '32%',
              }}
            />
          </div>
        );
      })}
      <button
        type="button"
        className="btn btn-primary btn-block outlined add-rule"
        onClick={() => fields.push({})}
      >
        <DixaIcon icon="plus" />
        Add Rule
      </button>
    </div>
  );
};

class ContentModalImpl extends Component<
  PropsT & FlowEmailContentModalReduxFormPropsT
> {
  @boundMethod
  handleSaveClick(values: ContentModalFormValues) {
    const payload: FlowEmailNodeContentPayloadT = {
      ...this.props.node.payload,
      rules: values.rules,
      rule_type: values.rule_type,
    };

    if (!this.props.node.payload || !this.props.node.payload.match_node_id) {
      payload.match_node_id = getRequestID();
    } else {
      payload.match_node_id = this.props.node.payload.match_node_id;
    }

    if (
      !this.props.node.payload ||
      !this.props.node.payload.non_match_node_id
    ) {
      payload.non_match_node_id = getRequestID();
    } else {
      payload.non_match_node_id = this.props.node.payload.non_match_node_id;
    }

    const nodeOptions: FlowEmailNodeContentT = {
      ...this.props.node,
      badges: [],
      childIds: [],
      icon: 'i-align-left',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.CONTENT,
      overlay: [],
      payload: payload,
      text: 'Content',
    };

    this.props.replaceNode(nodeOptions);
    this.props.triggerClose();
  }

  @boundMethod
  closeModal() {
    const { destroy, triggerClose } = this.props;
    destroy();
    triggerClose();
  }

  render() {
    const { node, nodeLabel, handleSubmit } = this.props;

    return (
      <div className="email-content modal-emailflow-holder">
        <Form onSubmit={handleSubmit(this.handleSaveClick)}>
          <ModalHead title={nodeLabel} onClose={this.closeModal}>
            The content setting can be used to change what should happen to an
            email conversation based on the content of the email.
          </ModalHead>
          <ModalBody>
            <FlexBox flexDirection="column">
              <Field
                name="rule_type"
                isRequired={true}
                displayName="Type"
                component={SelectField}
                className="form-control"
                label="Target"
              >
                <option value="any">
                  If any of the following conditions are met
                </option>
                <option value="all">
                  If all of the following conditions are met
                </option>
              </Field>
              <FieldArray name="rules" component={ListContent} />
            </FlexBox>
          </ModalBody>
          <ModalFoot
            confirmOnClick={handleSubmit(this.handleSaveClick)}
            confirmName={getConfirmButtonVerb(node)}
            cancelOnClick={this.closeModal}
          />
        </Form>
      </div>
    );
  }
}

export const ContentModal = connect<
  FlowEmailContentModalStatePropsT,
  Record<string, never>,
  FlowEmailContentModalOwnPropsT,
  RootReducerStateT
>(
  (
    _state: RootReducerStateT,
    ownProps: FlowEmailContentModalOwnPropsT,
  ): FlowEmailContentModalStatePropsT => {
    let payload: FlowEmailNodeContentPayloadT = {
      rules: [
        {
          type: 'subject',
          rule: 'contains',
          match: '',
        },
      ],
      rule_type: 'any',
    };

    if (ownProps.node.payload !== null) {
      payload = ownProps.node.payload;
    }

    return {
      payload,
      initialValues: payload,
    };
  },
)(
  reduxForm<
    ContentModalFormValues,
    FlowEmailContentModalStatePropsT & FlowEmailContentModalOwnPropsT
  >({
    form: 'emailFlowContentForm', // A unique identifier for this form.
    enableReinitialize: true,
    validate,
  })(ContentModalImpl),
);
